/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import type { TFunction } from 'i18next';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  ReactHookFormHiddenInput,
  errorMessages,
  useDraftFormHelpers,
} from '@opengeoweb/form-fields';
import { ToggleMenu, dateUtils } from '@opengeoweb/shared';
import { Edit, Visibility, Delete, Add } from '@opengeoweb/theme';
import { useWarningsTranslation } from '../../../utils/i18n';

export const formatDate = (lastUpdatedTime: string): string =>
  dateUtils.dateToString(
    dateUtils.isoStringToDate(lastUpdatedTime, false),
    `${dateUtils.DATE_FORMAT_DATEPICKER} 'UTC'`,
  )!;

const getObjectName = (
  geoJSON: GeoJSON.FeatureCollection,
  objectName: string,
  t: TFunction,
): string => {
  if (geoJSON) {
    return objectName || t('object-manager-object-selected');
  }
  return t('object-manager-no-object-selected');
};

export const AreaField: React.FC<{
  onEditObject: () => void;
  onViewObject: () => void;
  onDeleteObject: () => void;
  onAddObject: () => void;
  isDisabled?: boolean;
  drawActions?: React.ReactNode;
}> = ({
  onEditObject = (): void => {},
  onViewObject = (): void => {},
  onDeleteObject = (): void => {},
  onAddObject = (): void => {},
  isDisabled = false,
  drawActions,
}) => {
  const { t } = useWarningsTranslation();
  const {
    getValues,
    formState: { errors },
  } = useFormContext();
  const { isRequired } = useDraftFormHelpers();
  const lastUpdatedTime = getValues('object.lastUpdatedTime');
  const geoJSON = getValues('areas[0].geoJSON');
  const objectName = getObjectName(
    geoJSON,
    getValues('areas[0].objectName'),
    t,
  );

  const hasErrors = errors?.areas !== undefined;
  const hasExistingGeoJSON = geoJSON !== undefined && geoJSON !== null;

  const areaButtonTitle = t(
    !hasExistingGeoJSON
      ? 'warning-button-add-object'
      : 'warning-button-replace-object',
  );

  return (
    <>
      <Typography
        color="text.secondary"
        sx={{
          fontSize: '0.75rem',
          fontWeight: 'normal',
          lineHeight: 1.83,
          paddingLeft: 1.5,
          ...(hasErrors && {
            color: 'geowebColors.captions.captionError.color',
          }),
        }}
        variant="h3"
        {...(hasErrors && {
          'aria-errormessage': errorMessages.required,
        })}
      >
        {t('warning-area')}
      </Typography>
      <Card elevation={0} sx={{ ...(!geoJSON && { background: 'none' }) }}>
        <CardContent
          sx={{
            position: 'relative',
            padding: 1,
            '&:last-child': { paddingBottom: 1 },
          }}
        >
          <Typography
            role="heading"
            sx={{
              color: 'text.primary',
              fontSize: '0.75rem',
              fontWeight: 'normal',
              lineHeight: 1.83,
            }}
            variant="h4"
          >
            {objectName}
          </Typography>
          <Typography sx={{ color: 'text.secondary' }} variant="body2">
            {lastUpdatedTime ? formatDate(lastUpdatedTime) : ''}
          </Typography>

          {geoJSON ? (
            <ToggleMenu
              buttonSx={{
                position: 'absolute',
                right: 0,
                margin: 'auto',
                top: 0,
                bottom: 0,
                marginRight: '8px',
              }}
              isDisabled={true}
              menuItems={[
                {
                  text: t('object-manager-button-edit'),
                  action: onEditObject,
                  icon: <Edit />,
                },
                {
                  text: t('object-manager-button-view'),
                  action: onViewObject,
                  icon: <Visibility />,
                },
                {
                  text: t('object-manager-button-delete'),
                  action: onDeleteObject,
                  icon: <Delete />,
                },
              ]}
              menuPosition="bottom"
              menuTitle={t('object-manager-options-menu')}
              tooltipTitle={t('object-manager-options-menu')}
            />
          ) : null}
        </CardContent>
      </Card>
      <ReactHookFormHiddenInput
        name="areas[0].geoJSON"
        rules={{
          validate: { isRequired },
        }}
      />
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          marginTop: 1,
        }}
      >
        <Button
          disabled={isDisabled}
          onClick={onAddObject}
          size="small"
          startIcon={<Add />}
          variant="tertiary"
        >
          {areaButtonTitle}
        </Button>
        {drawActions}
      </Box>
    </>
  );
};
